import { Component, inject } from '@angular/core';
import { HeaderComponent } from '../../header/header.component';
import { SidebarComponent } from '../../../../../src/app/components/sidebar/sidebar.component';
import { Router, RouterModule } from '@angular/router';
import { RestaurantService } from '../../../services/restaurant.service';
import { AuthService } from '../../../services/auth.service';
import { FNFResponse } from '../../../models/response';
import { SubscriptionService } from '../../../services/subscription.service';
import { PublishedAgreementModalComponent } from '../../modals/published-agreement-modal/published-agreement-modal.component';
import { AgreementSuccessModalComponent } from '../../modals/agreement-success-modal/agreement-success-modal.component';
import { ToastrService } from 'ngx-toastr';
import { ChoosePosModalComponent } from '../../modals/choose-pos-modal/choose-pos-modal.component';
import { WhatsNewSettingsModalComponent } from '../../modals/whats-new-settings-modal/whats-new-settings-modal.component';
import { getLoggedInUser, isPlatformSupportUser } from '../../../utils/user-role.util';

/** Remembers, per user on this browser, that the Settings merge was announced. */
const WHATS_NEW_SETTINGS_KEY = 'whatsNew.settingsUnified';
import { FEATURE_FLAG_KEYS, FeatureFlag, isFeatureActive } from '../../../utils/feature-flag.util';
import { CHOOSE_POS_DISMISSED_KEY, isPosNotSelected } from '../../../utils/pos-provider.util';

@Component({
  selector: 'app-home-page',
  standalone: true,
  imports: [
    RouterModule,
    HeaderComponent,
    SidebarComponent,
    PublishedAgreementModalComponent,
    AgreementSuccessModalComponent,
    ChoosePosModalComponent,
    WhatsNewSettingsModalComponent,
  ],
  templateUrl: './home-page.component.html',
  styleUrl: './home-page.component.css',
})
export class HomePageComponent {
  restaurantService = inject(RestaurantService);
  subscriptionService = inject(SubscriptionService);
  private authService = inject(AuthService);
  private toastrService = inject(ToastrService);
  private router = inject(Router);

  /**
   * Gate shown once per session (not per `/main` child navigation, since this
   * component is the shell that persists across them) for a `published` but
   * not yet `activated` restaurant. Resets naturally on the next login,
   * since a fresh login recreates this component.
   */
  showPublishedAgreement = false;
  agreeingToPublishedAgreement = false;
  showAgreementSuccess = false;
  currentRestaurant: any;
  /**
   * "Choose how you'll take orders" — for a restaurant with no POS chosen yet
   * (`pos_provider: 'none'`). "Later" hides it until the next login (the
   * dismissal lives in sessionStorage and AuthService.logout clears it).
   */
  showChoosePos = false;
  enablingFnf = false;
  canConnectPos = false;
  private isRestaurantAdmin = false;
  /**
   * One-time "To Go, Payments and Settings are now one page" dialog, for an
   * activated restaurant's staff (the only ones who had those pages). Waits
   * behind the agreement and choose-POS prompts, so it never stacks on them.
   */
  showWhatsNewSettings = false;

  ngOnInit() {
    this.getRestaurantFeatureFlags();
    this.getRestaurantSubscription();
    this.getRestaurantPaymentMethod();
    let currentUser: any;
    if (localStorage.getItem('user')) {
      let user: any = localStorage.getItem('user');
      if (user) {
        currentUser = JSON.parse(user);
      }
    }
    this.isRestaurantAdmin = currentUser?.user_role?.toLowerCase() == 'restaurant_admin';
    this.restaurantService.restaurantFeatureFlags$.subscribe((flags: FeatureFlag[] | null) => {
      // The Square / Clover / Swirepay connect flow lives on the Hardware page,
      // which redirects away when HARDWARE is off — only offer it when reachable.
      this.canConnectPos =
        isFeatureActive(flags, FEATURE_FLAG_KEYS.HARDWARE) &&
        (isFeatureActive(flags, FEATURE_FLAG_KEYS.SQUARE) ||
          isFeatureActive(flags, FEATURE_FLAG_KEYS.CLOVER) ||
          isFeatureActive(flags, FEATURE_FLAG_KEYS.SWIREPAY));
    });
    this.restaurantService.currentRestaurantDetails$.subscribe((restaurant: any) => {
      this.currentRestaurant = restaurant;
      if (
        restaurant?.status === 'published' &&
        !restaurant?.is_agreement_signed &&
        currentUser.user_role.toLowerCase() == 'restaurant_admin'
      ) {
        this.showPublishedAgreement = true;
      }
      if (
        restaurant?.status === 'published' &&
        !restaurant?.request_activation &&
        currentUser.user_role.toLowerCase() == 'restaurant_admin' &&
        restaurant?.pos_provider == 'fnf' &&
        !this.router.url.includes('/hardware') &&
        !this.showPublishedAgreement
      ) {
        this.showAgreementSuccess = true;
      }
      this.evaluateChoosePos();
      this.evaluateWhatsNewSettings();
    });
  }

  private evaluateWhatsNewSettings() {
    this.showWhatsNewSettings =
      this.currentRestaurant?.status === 'activated' &&
      !isPlatformSupportUser() &&
      !this.showPublishedAgreement &&
      !this.showAgreementSuccess &&
      !this.showChoosePos &&
      !this.hasSeenWhatsNewSettings();
  }

  private whatsNewSettingsKey(): string {
    const user = getLoggedInUser();
    return `${WHATS_NEW_SETTINGS_KEY}:${user?.user_id || user?.email || 'user'}`;
  }

  private hasSeenWhatsNewSettings(): boolean {
    try {
      return localStorage.getItem(this.whatsNewSettingsKey()) === '1';
    } catch {
      // Storage blocked: better to skip the announcement than repeat it.
      return true;
    }
  }

  onWhatsNewSettingsDismiss() {
    this.showWhatsNewSettings = false;
    try {
      localStorage.setItem(this.whatsNewSettingsKey(), '1');
    } catch {
      // Storage blocked: it simply may show again next time.
    }
  }

  onWhatsNewOpenSettings() {
    this.onWhatsNewSettingsDismiss();
    this.router.navigate(['main/settings']);
  }

  /** Shows the choose-POS prompt for a `none` restaurant's admin, unless the
   * agreement gate is up or it was dismissed earlier this session. */
  private evaluateChoosePos() {
    this.showChoosePos =
      this.isRestaurantAdmin &&
      isPosNotSelected(this.currentRestaurant?.pos_provider) &&
      !this.showPublishedAgreement &&
      !this.router.url.includes('/hardware') &&
      !this.isChoosePosDismissed();
  }

  private isChoosePosDismissed(): boolean {
    try {
      return (
        sessionStorage.getItem(CHOOSE_POS_DISMISSED_KEY) === this.currentRestaurant?.restaurant_id
      );
    } catch {
      return false;
    }
  }

  onEnableFnfFromPrompt() {
    this.enablingFnf = true;
    this.restaurantService.enableForkAndFind(this.currentRestaurant).subscribe({
      next: (response: FNFResponse<any>) => {
        this.enablingFnf = false;
        if (!response.data && response.error) {
          this.toastrService.error(
            response.error.error?.message ||
              response.error.message ||
              'Unable to enable Fork & Find.',
            'Error',
          );
          return;
        }
        // enableForkAndFind pushed the `fnf` restaurant, which already hid the prompt.
        this.showChoosePos = false;
        this.toastrService.success(
          'Fork & Find enabled. Our support team will activate your restaurant within 1-2 working days.',
          'Success',
        );
      },
      error: () => {
        this.enablingFnf = false;
        this.toastrService.error(
          'Unable to complete your action. Please try again later.',
          'Error',
        );
      },
    });
  }

  onConnectPosFromPrompt() {
    // Counts as a dismissal: the connect flow continues on the Hardware page.
    this.onChoosePosLater();
    this.router.navigate(['main/hardware']);
  }

  onChoosePosLater() {
    this.showChoosePos = false;
    try {
      sessionStorage.setItem(CHOOSE_POS_DISMISSED_KEY, this.currentRestaurant?.restaurant_id ?? '');
    } catch {
      // Storage blocked — the prompt simply stays hidden until this shell is recreated.
    }
  }

  onAgreePublishedAgreement() {
    this.agreeingToPublishedAgreement = true;
    this.restaurantService.updateAgreementDocument().subscribe({
      next: (response: FNFResponse<any>) => {
        this.agreeingToPublishedAgreement = false;
        if (!response.data && response.error) {
          this.toastrService.error(
            response.error.error?.message || response.error.message || 'No data found!',
            'Error',
          );
          return;
        } else if (!response.data) {
          this.toastrService.error('No data found!', 'Error');
          return;
        }
        this.currentRestaurant.is_agreement_signed = true;
        this.restaurantService.currentRestaurantDetails$.next(this.currentRestaurant);
        this.showPublishedAgreement = false;
        if (isPosNotSelected(this.currentRestaurant?.pos_provider)) {
          this.evaluateChoosePos();
        } else {
          this.showAgreementSuccess = true;
        }
      },
      error: () => {
        this.agreeingToPublishedAgreement = false;
        this.toastrService.error(
          'Unable to complete your action. Please try again later.',
          'Error',
        );
      },
    });
  }

  onCancelPublishedAgreement() {
    this.authService.logout();
    this.router.navigate(['login']);
  }

  onGoToHardware() {
    this.showAgreementSuccess = false;
    this.router.navigate(['main/hardware']);
  }

  onIntegrateLater() {
    this.showAgreementSuccess = false;
  }

  getRestaurantFeatureFlags() {
    this.restaurantService.getRestaurantFeatureFlags().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          console.log(response.error.error?.message || response.error.message, 'Error');
          return;
        } else if (!response.data) {
          console.log('No data found!', 'Error');
          return;
        }
        let featureFlags = response.data?.flags;
        if (featureFlags) {
          this.restaurantService.restaurantFeatureFlags$.next(featureFlags);
        }
      },
      error: () => {
        console.log('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  getRestaurantSubscription() {
    this.subscriptionService.getMySubscription().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          console.log(response.error.error?.message || response.error.message, 'Error');
          return;
        } else if (!response.data) {
          console.log('No data found!', 'Error');
          return;
        }
        let subscription = response.data;
        if (subscription) {
          this.restaurantService.restaurantSubscription$.next(subscription);
          this.resolveCurrentPlanName(subscription.planId);
        }
      },
      error: () => {
        console.log('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }

  /** Sidebar and route guards gate on the plan's *name* (Free/Standard/Premium),
   * not its planId, so resolve it once here rather than in each consumer. */
  resolveCurrentPlanName(planId: string) {
    if (!planId) {
      return;
    }
    this.subscriptionService.getPlan(planId).subscribe({
      next: (response: FNFResponse<any>) => {
        if (response.data?.name) {
          this.restaurantService.currentPlanName$.next(response.data.name);
        }
      },
      error: () => {
        console.log('Unable to resolve the current subscription plan.', 'Error');
      },
    });
  }

  getRestaurantPaymentMethod() {
    this.subscriptionService.getMyPaymentMethod().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          console.log(response.error.error?.message || response.error.message, 'Error');
          return;
        } else if (!response.data) {
          console.log('No data found!', 'Error');
          return;
        }
        let paymentMethod = response.data;
        if (paymentMethod) {
          this.restaurantService.restaurantPaymentMethod$.next(paymentMethod);
        }
      },
      error: () => {
        console.log('Unable to complete your action. Please try again later.', 'Error');
      },
    });
  }
}
